<template>
  <BlockFieldset class="project-bank-self-employment">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <div class="bank-section-grid bank-section-grid--paired">
        <div class="label">{{ $t('bankSelfEmploymentNetIncome') }}</div>
        <div>
          <InputNumber
            id="bankSelfEmploymentNetIncome"
            :value="project.bank.selfEmploymentNetIncome"
            @input="setBankField('selfEmploymentNetIncome', $event)"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" €"
            locale="de-DE"
            :placeholder="$t('bankSelfEmploymentNetIncome')"
          />
        </div>
        <template v-for="row in ATTACHMENT_ROWS">
          <div class="label" :key="`${row.attachmentType}-label`">{{ $t(row.labelKey) }}</div>
          <div :key="row.attachmentType">
            <BankAttachmentUpload
              :project="project"
              :attachmentType="row.attachmentType"
              @changed="reloadBlock"
            />
          </div>
        </template>
      </div>
      <div v-if="!isBlockReadOnly" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('bankSectionSelfEmployment') }) }}
        </b-button>
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions } from 'vuex';
import InputNumber from 'primevue/inputnumber';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import BankAttachmentUpload from '@/components/Projects/BankAttachmentUpload';
import { BANK_ATTACHMENT_TYPES } from '@/helpers/bankAttachmentTypes';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { ProjectBankBlockMixin } from '@/mixins/projectBankBlock';
import { PROJECT_BANK_SELF_EMPLOYMENT_FIELDS } from './projectBankSelfEmploymentFragment';
import { toProjectBankSelfEmploymentInput } from './projectBankSelfEmploymentInput';

/** Documents of the section in the order of the previous tab. */
const ATTACHMENT_ROWS = [
  {
    labelKey: 'bankAttachmentBwaSusaFullYear',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_BWA_SUSA_FULL_YEAR,
  },
  {
    labelKey: 'bankAttachmentAnnualFinancialStatement',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_ANNUAL_FINANCIAL_STATEMENT,
  },
  { labelKey: 'bankAttachmentCurrentBwa', attachmentType: BANK_ATTACHMENT_TYPES.BANK_CURRENT_BWA },
];

/**
 * Block project-bank-self-employment ("Einkünfte aus selbst. Tätigkeit", tab Bank, see feature page projekt-bank): the net income, saved with the own button, and the documents of the section.
 * `project` is the block data (`Project.blocks.bankSelfEmployment`); an upload or a delete reloads it. On
 * the unsaved project it shows only a hint. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectBankSelfEmployment',
  components: { BankAttachmentUpload, BlockFieldset, InputNumber },
  mixins: [AccessBlockMixin, BlockSaveMixin, ProjectBankBlockMixin],
  bankBlockField: 'bankSelfEmployment',
  bankBlockFragment: PROJECT_BANK_SELF_EMPLOYMENT_FIELDS,
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return { ATTACHMENT_ROWS };
  },
  computed: {
    blockInput() {
      return toProjectBankSelfEmploymentInput(this.project);
    },
  },
  methods: {
    ...mapActions(['updateProjectBankSelfEmployment']),
    saveBlockInput(input) {
      return this.updateProjectBankSelfEmployment({ projectNumber: this.project.number, input });
    },
    setBankField(field, value) {
      this.$set(this.project.bank, field, value);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../styles/bankSectionGrid';
</style>
